/**
 * Pure chart helpers for the Usage Report charts.
 *
 * `UsageReportViewer` feeds the raw `getUsageReport` entries through
 * `bucketSlices` (top-N + "Other" readability bucketing) before handing them
 * to Recharts, and uses `sliceShare` for the per-slice percentage shown in the
 * legend/tooltip. The per-day line charts go through `fillDailyTrend`
 * (distinct users per day) and `buildDailySeries` (one line per dimension
 * value). Every function is pure (no React, no I/O) so the chart math is
 * testable in isolation.
 *
 * See design "Components and Interfaces > 5. Usage_Report_Viewer — Pure chart
 * helpers (`src/audit/usageChart.ts`)".
 *
 * Requirements: 2.1, 2.2, 2.4, 2.5
 */

/**
 * One entry of the Usage_Report_API result: a Dimension_Value label and its
 * Operation_Count (matches the `UsageReportEntry` GraphQL wire shape).
 */
export type UsageEntry = { dimension_value: string; operation_count: number };

/**
 * One pie slice: its display label (the Dimension_Value, or "Other"), its
 * count, and whether it is the combined "Other" bucket (Req 2.2).
 */
export type Slice = { label: string; count: number; isOther: boolean };

/**
 * Maximum number of individual slices displayed before the remaining entries
 * collapse into a single "Other" slice (Req 2.2 cutoff).
 */
export const MAX_SLICES = 10;

/**
 * Label of the bucket slice that combines the remaining entries (Req 2.2).
 * Also the label the `get_usage_report` Lambda gives the per-day remainder
 * row of the daily series (`DAILY_SERIES_OTHER_LABEL`), so the pie's bucket
 * and the line chart's bucket read the same.
 */
export const OTHER_LABEL = "Other";

/**
 * Order entries by Operation_Count descending; entries with equal counts are
 * ordered by case-insensitive alphabetical order of their labels (Req 2.2).
 */
function compareEntries(a: UsageEntry, b: UsageEntry): number {
  if (a.operation_count !== b.operation_count) {
    return b.operation_count - a.operation_count;
  }
  const aLabel = a.dimension_value.toLowerCase();
  const bLabel = b.dimension_value.toLowerCase();
  if (aLabel < bLabel) return -1;
  if (aLabel > bLabel) return 1;
  return 0;
}

/**
 * Bucket usage entries into pie slices.
 *
 * Entries are sorted by Operation_Count descending with ties broken by
 * case-insensitive label ascending (Req 2.2). When there are at most
 * `maxSlices` entries, every entry becomes its own slice and no "Other" slice
 * is produced (Req 2.1). When there are more, the first `maxSlices` entries
 * become individual slices and all remaining entries collapse into exactly one
 * additional "Other" slice (`isOther: true`) whose count is the sum of the
 * remaining entries' counts (Req 2.2). In both cases the slice counts sum
 * exactly to the total of the input counts (Req 2.5).
 */
export function bucketSlices(
  entries: UsageEntry[],
  maxSlices: number = MAX_SLICES
): Slice[] {
  const sorted = [...entries].sort(compareEntries);
  const toSlice = (entry: UsageEntry): Slice => ({
    label: entry.dimension_value,
    count: entry.operation_count,
    isOther: false,
  });
  if (sorted.length <= maxSlices) {
    return sorted.map(toSlice);
  }
  const otherCount = sorted
    .slice(maxSlices)
    .reduce((sum, entry) => sum + entry.operation_count, 0);
  return [
    ...sorted.slice(0, maxSlices).map(toSlice),
    { label: OTHER_LABEL, count: otherCount, isOther: true },
  ];
}

/**
 * Percentage share of `count` in `total`, rounded to exactly one decimal place
 * (e.g. 12.3 for 12.34...%) (Req 2.4).
 *
 * `total` is expected to be positive: the viewer renders a chart (and hence
 * computes shares) only when at least one entry with a positive count exists —
 * the zero-total case renders the empty state instead (Req 2.6).
 */
export function sliceShare(count: number, total: number): number {
  const percentage = (count / total) * 100;
  return Math.round(percentage * 10) / 10;
}

/**
 * Sub-selection of the operation dimension: everything, only GraphQL
 * queries, or only GraphQL mutations.
 */
export type OperationTypeFilter = "all" | "query" | "mutation";

/**
 * The type suffixes the aggregation job appends to operation
 * Dimension_Values (`operation_value` in SPBFinnDag's aggregate.py), e.g.
 * "submitQuestion2 (mutation)" / "getUsers (query)". Must stay in sync
 * with that format.
 */
export const OPERATION_TYPE_SUFFIXES: Record<"query" | "mutation", string> = {
  query: " (query)",
  mutation: " (mutation)",
};

/**
 * Filter operation-dimension entries by GraphQL operation type.
 *
 * `"all"` returns the entries unchanged (type suffixes stay visible so the
 * combined view remains distinguishable). `"query"` / `"mutation"` keep
 * only entries carrying the matching type suffix and strip the suffix from
 * the label — the sub-selection already names the type, so repeating it on
 * every slice would be noise. Entries without a recognized suffix (the
 * "unknown" bucket, or rows aggregated before type suffixes existed)
 * appear only under `"all"`, since their type cannot be determined.
 *
 * Pure function (no React, no I/O) so the filtering is testable in
 * isolation; callers bucket the RESULT, so the top-10 + "Other" view is
 * computed within the filtered set.
 */
export function filterOperationEntries<T extends UsageEntry>(
  entries: T[],
  filter: OperationTypeFilter
): T[] {
  if (filter === "all") {
    return entries;
  }
  const suffix = OPERATION_TYPE_SUFFIXES[filter];
  return entries
    .filter((entry) => entry.dimension_value.endsWith(suffix))
    .map((entry) => ({
      ...entry,
      dimension_value: entry.dimension_value.slice(0, -suffix.length),
    }));
}

/**
 * One point of the distinct-users-per-day trend line (matches the
 * `UsageTrendPoint` GraphQL wire shape).
 */
export type TrendPoint = { day: string; distinct_users: number };

/** Milliseconds per UTC day, for date arithmetic over "YYYY-MM-DD" keys. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Format an epoch-ms UTC instant as its "YYYY-MM-DD" calendar day. */
function toIsoDay(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

/**
 * First day of the inclusive reporting window ending at `endDay`: the day
 * `windowDays - 1` days before it (mirrors the server's `window_bounds`).
 * Returns an empty string when `endDay` does not parse as "YYYY-MM-DD".
 */
export function windowStartDay(endDay: string, windowDays: number): string {
  const endMs = Date.parse(`${endDay}T00:00:00Z`);
  if (Number.isNaN(endMs)) {
    return "";
  }
  return toIsoDay(endMs - (windowDays - 1) * MS_PER_DAY);
}

/**
 * Zero-fill the distinct-users trend over an inclusive UTC day window.
 *
 * The API returns one point per day WITH usage; days without rows are
 * absent. A line chart drawn over the sparse points would silently
 * interpolate across gaps, so this helper produces exactly one point per
 * calendar day in `[startDay, endDay]` (inclusive, ascending): the API's
 * count where a point exists, `0` otherwise. Points outside the window and
 * malformed day keys are ignored; when `startDay`/`endDay` do not parse as
 * "YYYY-MM-DD" days, or the window is inverted, an empty array is returned.
 *
 * Pure function (no wall clock, no I/O) so the trend math is testable in
 * isolation.
 */
export function fillDailyTrend(
  points: TrendPoint[],
  startDay: string,
  endDay: string
): TrendPoint[] {
  const startMs = Date.parse(`${startDay}T00:00:00Z`);
  const endMs = Date.parse(`${endDay}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
    return [];
  }
  const byDay = new Map<string, number>();
  for (const point of points) {
    byDay.set(point.day, point.distinct_users);
  }
  const filled: TrendPoint[] = [];
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const day = toIsoDay(ms);
    filled.push({ day, distinct_users: byDay.get(day) ?? 0 });
  }
  return filled;
}

/**
 * One row of the per-value daily series (matches the `UsageDailyEntry`
 * GraphQL wire shape): a UTC day, a Dimension_Value (or the `OTHER_LABEL`
 * bucket the server folds the non-top values into), and that value's
 * Operation_Count for the day. Structurally a `UsageEntry` plus `day`, so
 * the operation-type filter applies to it unchanged.
 */
export type DailyEntry = UsageEntry & { day: string };

/**
 * One Recharts row of the multi-line daily chart: the day plus one numeric
 * column per series label (the labels are the `DailySeries.labels`). The
 * `day` key is reserved for the x-axis; dimension values never collide with
 * it in practice (operation values carry a type suffix, channels are a fixed
 * set, products and teams are descriptive names).
 */
export type DailySeriesRow = { [column: string]: number | string; day: string };

/** The multi-line daily chart's data: series labels plus zero-filled rows. */
export type DailySeries = {
  /**
   * Series labels in legend order: by total over the window descending,
   * ties by case-insensitive label ascending (the pie's slice order), with
   * the `OTHER_LABEL` bucket always last whatever its total.
   */
  labels: string[];
  /**
   * Exactly one row per calendar day in the window (inclusive, ascending),
   * every label present on every row (`0` where the value saw no usage).
   * Empty when there are no series to draw.
   */
  rows: DailySeriesRow[];
};

/**
 * Order series labels by window total descending; ties are ordered by
 * case-insensitive label ascending; the "Other" bucket is always last.
 */
function compareSeriesLabels(
  totals: Map<string, number>
): (a: string, b: string) => number {
  return (a, b) => {
    if (a === OTHER_LABEL) return b === OTHER_LABEL ? 0 : 1;
    if (b === OTHER_LABEL) return -1;
    const totalA = totals.get(a) ?? 0;
    const totalB = totals.get(b) ?? 0;
    if (totalA !== totalB) return totalB - totalA;
    const lowerA = a.toLowerCase();
    const lowerB = b.toLowerCase();
    if (lowerA < lowerB) return -1;
    if (lowerA > lowerB) return 1;
    return 0;
  };
}

/**
 * Pivot the sparse per-(day, value) daily entries into zero-filled wide rows
 * for a multi-line chart over an inclusive UTC day window.
 *
 * The API returns one entry per (day, value) WITH usage; (day, value) pairs
 * without rows are absent. A line chart drawn over the sparse entries would
 * interpolate across gaps, so this helper produces exactly one row per
 * calendar day in `[startDay, endDay]` (inclusive, ascending) carrying every
 * series label: the API's count where an entry exists, `0` otherwise.
 * Several entries for the same (day, value) are summed. Entries outside the
 * window and malformed day keys are ignored; when `startDay`/`endDay` do not
 * parse as "YYYY-MM-DD" days, or the window is inverted, or no in-window
 * entry exists, `{ labels: [], rows: [] }` is returned (nothing to draw).
 *
 * Pure function (no wall clock, no I/O) so the chart math is testable in
 * isolation.
 */
export function buildDailySeries(
  entries: DailyEntry[],
  startDay: string,
  endDay: string
): DailySeries {
  const startMs = Date.parse(`${startDay}T00:00:00Z`);
  const endMs = Date.parse(`${endDay}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
    return { labels: [], rows: [] };
  }
  const countByDayAndLabel = new Map<string, Map<string, number>>();
  const totals = new Map<string, number>();
  for (const entry of entries) {
    const dayMs = Date.parse(`${entry.day}T00:00:00Z`);
    if (Number.isNaN(dayMs) || dayMs < startMs || dayMs > endMs) continue;
    const day = toIsoDay(dayMs);
    const label = entry.dimension_value;
    const byLabel = countByDayAndLabel.get(day) ?? new Map<string, number>();
    byLabel.set(label, (byLabel.get(label) ?? 0) + entry.operation_count);
    countByDayAndLabel.set(day, byLabel);
    totals.set(label, (totals.get(label) ?? 0) + entry.operation_count);
  }
  if (totals.size === 0) {
    return { labels: [], rows: [] };
  }
  const labels = [...totals.keys()].sort(compareSeriesLabels(totals));
  const rows: DailySeriesRow[] = [];
  for (let ms = startMs; ms <= endMs; ms += MS_PER_DAY) {
    const day = toIsoDay(ms);
    const byLabel = countByDayAndLabel.get(day);
    const row: DailySeriesRow = { day };
    for (const label of labels) {
      row[label] = byLabel?.get(label) ?? 0;
    }
    rows.push(row);
  }
  return { labels, rows };
}
